/**
 * Sandbox op model (doc 147 §6.5 What-If Schema Sandbox).
 *
 * The backend (`functions/src/sandboxes.ts`) stores `ops: unknown[]` opaquely
 * — this module owns the op schema, applies ops to produce a draft manifest,
 * and computes per-entity diffs the editor renders.
 *
 * Pure module; backbone of the SandboxEditorView. Unit-tested exhaustively.
 */
import type {
  AtlasManifest,
  CloudFunction,
  CloudFunctionKind,
  Domain,
  Entity,
  EventSafetyTier,
  EventType,
  Feature,
  Field,
  FirestorePath,
  Reference,
  ReferenceKind,
  StateMachine,
  Surface,
  SurfaceType,
  TagDefinition,
} from './types.js';

export type SandboxOp =
  | {
      kind: 'add-field';
      entityId: string;
      field: { name: string; type: string; optional: boolean; description?: string };
    }
  | { kind: 'remove-field'; entityId: string; fieldName: string }
  | { kind: 'rename-field'; entityId: string; from: string; to: string }
  | { kind: 'change-type'; entityId: string; fieldName: string; newType: string }
  | { kind: 'create-entity'; entityId: string; name: string; domain: string; description: string }
  | { kind: 'create-domain'; domainId: string; name: string; color: string; description: string }
  | { kind: 'create-reference'; from: string; to: string; fromField: string; referenceKind: ReferenceKind }
  | { kind: 'create-surface'; surfaceId: string; surfaceType: SurfaceType; domain: string; hub: string }
  | { kind: 'create-cloud-function'; name: string; functionKind: CloudFunctionKind; domain: string }
  | { kind: 'create-event'; eventType: string; category: string; safetyTier: EventSafetyTier }
  | { kind: 'create-path'; entityId: string; path: string; retention: string }
  | { kind: 'create-tag'; tag: string; description: string }
  | { kind: 'create-state-machine'; machineId: string; name: string; domain: string }
  | { kind: 'update-entity'; entityId: string; name?: string; domain?: string; description?: string }
  | {
      kind: 'update-surface';
      surfaceId: string;
      surfaceType?: SurfaceType;
      domain?: string;
      hub?: string;
      description?: string;
    }
  | {
      kind: 'update-cloud-function';
      name: string;
      functionKind?: CloudFunctionKind;
      domain?: string;
      description?: string;
      rationale?: string;
      triggerCondition?: string;
    }
  | {
      kind: 'update-event';
      type: string;
      category?: string;
      description?: string;
      businessTrigger?: string;
      retentionDays?: number;
    }
  | {
      kind: 'update-path';
      path: string;
      retention?: string;
      securityRuleNote?: string;
      owner?: string;
      quotaNotes?: string;
    }
  | {
      kind: 'add-payload-field';
      ownerKind: 'surface' | 'event';
      ownerId: string;
      field: { name: string; type: string; description?: string };
    }
  | { kind: 'remove-payload-field'; ownerKind: 'surface' | 'event'; ownerId: string; fieldName: string }
  | { kind: 'rename-payload-field'; ownerKind: 'surface' | 'event'; ownerId: string; from: string; to: string }
  | {
      kind: 'change-payload-field-type';
      ownerKind: 'surface' | 'event';
      ownerId: string;
      fieldName: string;
      newType: string;
    }
  | { kind: 'add-function-entity-ref'; name: string; arrayName: 'reads' | 'writes'; entityId: string }
  | { kind: 'remove-function-entity-ref'; name: string; arrayName: 'reads' | 'writes'; entityId: string }
  | { kind: 'add-event-carried-entity'; type: string; entityId: string }
  | { kind: 'remove-event-carried-entity'; type: string; entityId: string }
  | { kind: 'link-feature-flag'; featureId: string; flagId: string }
  | { kind: 'unlink-feature-flag'; featureId: string; flagId: string };

/** An op paired with its curation state (AI-proposed or user-accepted) and optional confidence score. */
export interface SandboxOpEntry {
  op: SandboxOp;
  /** 'proposed' if AI-drafted and awaiting accept/reject; 'accepted' if confirmed. 'readonly' is DERIVED at render time from sandbox.status. */
  state: 'proposed' | 'accepted';
  /** 0-1 confidence score, only meaningful when state === 'proposed'. Set by AI drafting. */
  confidence?: number;
}

/** One publish event, grouping every op that publish accepted at once. */
export interface PublishRecord {
  /** ISO timestamp — matches AtlasManifest.generatedAt's convention. */
  publishedAt: string;
  sandboxId: string;
  sandboxName: string;
  authorEmail: string;
  /** Optional: GitHub assigns PR numbers only after creation, but the ledger commit has to exist before the PR can be opened against it — the commit can't know its own PR number yet. */
  prNumber?: number;
  /** generatedFromCommit of the manifest this sandbox was reviewed against — enables staleness detection. */
  baseManifestCommit: string;
  ops: SandboxOp[];
}

/** packages/atlas-manifest/pending-ops.json's top-level shape. */
export interface PendingOpsLedger {
  schemaVersion: 1;
  entries: PublishRecord[];
}

/**
 * Migration-safe reader: normalizes raw SandboxOp[] or SandboxOpEntry[] into SandboxOpEntry[].
 * Existing persisted sandboxes have raw ops (no wrapper). This function treats them as all-accepted.
 * Already-wrapped entries (with state/confidence) are passed through as-is.
 * Invalid entries are dropped (robust to corruption, partial migrations, etc.).
 */
export function normalizeSandboxOps(raw: unknown[]): SandboxOpEntry[] {
  if (!Array.isArray(raw)) return [];
  const result: SandboxOpEntry[] = [];
  for (const item of raw) {
    // Case 1: already a SandboxOpEntry (has 'op' and 'state' fields)
    if (
      item !== null &&
      typeof item === 'object' &&
      'op' in item &&
      'state' in item &&
      typeof (item as Record<string, unknown>).state === 'string' &&
      ((item as Record<string, unknown>).state === 'proposed' || (item as Record<string, unknown>).state === 'accepted')
    ) {
      const entry = item as Record<string, unknown>;
      const op = entry.op;
      if (typeof op === 'object' && op !== null && 'kind' in op && validateOp(op as SandboxOp).ok) {
        result.push({
          op: op as SandboxOp,
          state: entry.state as 'proposed' | 'accepted',
          confidence: typeof entry.confidence === 'number' ? entry.confidence : undefined,
        });
      }
      // else: skip this entry, it's malformed
    }
    // Case 2: raw SandboxOp (has 'kind' field, passes validateOp)
    else if (item !== null && typeof item === 'object' && 'kind' in item) {
      const validation = validateOp(item as SandboxOp);
      if (validation.ok) {
        result.push({
          op: item as SandboxOp,
          state: 'accepted',
        });
      }
      // else: skip, not a valid op
    }
    // else: skip this item, neither op nor entry
  }
  return result;
}

export type ValidateResult = { ok: true } | { ok: false; reason: string };

/** Upper bound on any string field inside an op — covers identifiers,
 *  type expressions, descriptions. Generous but bounded so a megabyte
 *  paste through the raw-JSON editor can't blow up applyOps. (R1.H3) */
export const MAX_STRING_LEN = 256;

function bounded(value: string, label: string): ValidateResult {
  if (value.length > MAX_STRING_LEN) {
    return { ok: false, reason: `${label} exceeds ${MAX_STRING_LEN} chars` };
  }
  return { ok: true };
}

/** Closed-enum values for update-* validation — mirrors the real SurfaceType/CloudFunctionKind unions in types.ts. */
const VALID_SURFACE_TYPES = new Set([
  'card', 'chart', 'overlay', 'chip', 'ai', 'hero', 'list', 'sheet', 'banner', 'grid', 'other',
]);
const VALID_CLOUD_FUNCTION_KINDS = new Set(['callable', 'http', 'trigger', 'scheduled']);

/**
 * Validate an update-* op's free-text patch fields (bounded length) and
 * confirm at least one patch field besides the selector was supplied — an
 * empty patch is invalid, not a no-op-that-passes-validation.
 */
function validateUpdatePatch(
  op: Record<string, unknown>,
  selectorKey: string,
  patchKeys: readonly string[],
): ValidateResult {
  const present = patchKeys.filter((k) => op[k] !== undefined);
  if (present.length === 0) {
    return { ok: false, reason: `at least one field besides ${selectorKey} must be present` };
  }
  for (const key of present) {
    const value = op[key];
    if (typeof value === 'string') {
      const b = bounded(value, key);
      if (!b.ok) return b;
    }
  }
  return { ok: true };
}

/** Shallow structural validation — catches missing keys before applyOps runs. */
export function validateOp(op: SandboxOp): ValidateResult {
  if (typeof op !== 'object' || op === null) {
    return { ok: false, reason: 'op must be an object' };
  }
  const REQUIRES_ENTITY_ID = new Set<SandboxOp['kind']>([
    'add-field',
    'remove-field',
    'rename-field',
    'change-type',
    'create-entity',
    'create-path',
  ]);
  if (REQUIRES_ENTITY_ID.has(op.kind)) {
    if (!('entityId' in op) || typeof op.entityId !== 'string' || op.entityId === '') {
      return { ok: false, reason: 'entityId is required' };
    }
    const eb = bounded(op.entityId, 'entityId');
    if (!eb.ok) return eb;
  }
  switch (op.kind) {
    case 'add-field': {
      if (!op.field || typeof op.field !== 'object') return { ok: false, reason: 'field is required' };
      if (typeof op.field.name !== 'string' || op.field.name === '') {
        return { ok: false, reason: 'field.name is required' };
      }
      if (typeof op.field.type !== 'string' || op.field.type === '') {
        return { ok: false, reason: 'field.type is required' };
      }
      const nb = bounded(op.field.name, 'field.name');
      if (!nb.ok) return nb;
      const tb = bounded(op.field.type, 'field.type');
      if (!tb.ok) return tb;
      if (op.field.description !== undefined) {
        const db = bounded(op.field.description, 'field.description');
        if (!db.ok) return db;
      }
      return { ok: true };
    }
    case 'remove-field': {
      if (typeof op.fieldName !== 'string' || op.fieldName === '') {
        return { ok: false, reason: 'fieldName is required' };
      }
      return bounded(op.fieldName, 'fieldName');
    }
    case 'rename-field': {
      if (typeof op.from !== 'string' || op.from === '') {
        return { ok: false, reason: 'from is required' };
      }
      if (typeof op.to !== 'string' || op.to === '') {
        return { ok: false, reason: 'to is required' };
      }
      const fb = bounded(op.from, 'from');
      if (!fb.ok) return fb;
      return bounded(op.to, 'to');
    }
    case 'change-type': {
      if (typeof op.fieldName !== 'string' || op.fieldName === '') {
        return { ok: false, reason: 'fieldName is required' };
      }
      if (typeof op.newType !== 'string' || op.newType === '') {
        return { ok: false, reason: 'newType is required' };
      }
      const fb = bounded(op.fieldName, 'fieldName');
      if (!fb.ok) return fb;
      return bounded(op.newType, 'newType');
    }
    case 'create-entity': {
      if (typeof op.name !== 'string' || op.name === '') {
        return { ok: false, reason: 'name is required' };
      }
      if (typeof op.domain !== 'string' || op.domain === '') {
        return { ok: false, reason: 'domain is required' };
      }
      if (typeof op.description !== 'string' || op.description === '') {
        return { ok: false, reason: 'description is required' };
      }
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      const db = bounded(op.domain, 'domain');
      if (!db.ok) return db;
      return bounded(op.description, 'description');
    }
    case 'create-domain': {
      if (typeof op.domainId !== 'string' || op.domainId === '') {
        return { ok: false, reason: 'domainId is required' };
      }
      const idb = bounded(op.domainId, 'domainId');
      if (!idb.ok) return idb;
      if (typeof op.name !== 'string' || op.name === '') {
        return { ok: false, reason: 'name is required' };
      }
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      if (typeof op.color !== 'string' || op.color === '') {
        return { ok: false, reason: 'color is required' };
      }
      const cb = bounded(op.color, 'color');
      if (!cb.ok) return cb;
      if (typeof op.description !== 'string' || op.description === '') {
        return { ok: false, reason: 'description is required' };
      }
      return bounded(op.description, 'description');
    }
    case 'create-reference': {
      if (typeof op.from !== 'string' || op.from === '') return { ok: false, reason: 'from is required' };
      const fb = bounded(op.from, 'from');
      if (!fb.ok) return fb;
      if (typeof op.to !== 'string' || op.to === '') return { ok: false, reason: 'to is required' };
      const tb = bounded(op.to, 'to');
      if (!tb.ok) return tb;
      if (typeof op.fromField !== 'string' || op.fromField === '') {
        return { ok: false, reason: 'fromField is required' };
      }
      const ffb = bounded(op.fromField, 'fromField');
      if (!ffb.ok) return ffb;
      if (typeof op.referenceKind !== 'string' || (op.referenceKind as string) === '') {
        return { ok: false, reason: 'referenceKind is required' };
      }
      return { ok: true };
    }
    case 'create-surface': {
      if (typeof op.surfaceId !== 'string' || op.surfaceId === '') {
        return { ok: false, reason: 'surfaceId is required' };
      }
      const idb = bounded(op.surfaceId, 'surfaceId');
      if (!idb.ok) return idb;
      if (typeof op.surfaceType !== 'string' || (op.surfaceType as string) === '') {
        return { ok: false, reason: 'surfaceType is required' };
      }
      if (typeof op.domain !== 'string' || op.domain === '') return { ok: false, reason: 'domain is required' };
      const db = bounded(op.domain, 'domain');
      if (!db.ok) return db;
      if (typeof op.hub !== 'string' || op.hub === '') return { ok: false, reason: 'hub is required' };
      return bounded(op.hub, 'hub');
    }
    case 'create-cloud-function': {
      if (typeof op.name !== 'string' || op.name === '') return { ok: false, reason: 'name is required' };
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      if (typeof op.functionKind !== 'string' || (op.functionKind as string) === '') {
        return { ok: false, reason: 'functionKind is required' };
      }
      if (typeof op.domain !== 'string' || op.domain === '') return { ok: false, reason: 'domain is required' };
      return bounded(op.domain, 'domain');
    }
    case 'create-event': {
      if (typeof op.eventType !== 'string' || op.eventType === '') {
        return { ok: false, reason: 'eventType is required' };
      }
      const etb = bounded(op.eventType, 'eventType');
      if (!etb.ok) return etb;
      if (typeof op.category !== 'string' || op.category === '') {
        return { ok: false, reason: 'category is required' };
      }
      const cb = bounded(op.category, 'category');
      if (!cb.ok) return cb;
      if (typeof op.safetyTier !== 'number' || op.safetyTier < 0 || op.safetyTier > 3) {
        return { ok: false, reason: 'safetyTier must be 0-3' };
      }
      return { ok: true };
    }
    case 'create-path': {
      if (typeof op.path !== 'string' || op.path === '') return { ok: false, reason: 'path is required' };
      const pb = bounded(op.path, 'path');
      if (!pb.ok) return pb;
      if (typeof op.retention !== 'string' || op.retention === '') {
        return { ok: false, reason: 'retention is required' };
      }
      return bounded(op.retention, 'retention');
    }
    case 'create-tag': {
      if (typeof op.tag !== 'string' || op.tag === '') return { ok: false, reason: 'tag is required' };
      const tb = bounded(op.tag, 'tag');
      if (!tb.ok) return tb;
      if (typeof op.description !== 'string' || op.description === '') {
        return { ok: false, reason: 'description is required' };
      }
      return bounded(op.description, 'description');
    }
    case 'create-state-machine': {
      if (typeof op.machineId !== 'string' || op.machineId === '') {
        return { ok: false, reason: 'machineId is required' };
      }
      const mb = bounded(op.machineId, 'machineId');
      if (!mb.ok) return mb;
      if (typeof op.name !== 'string' || op.name === '') return { ok: false, reason: 'name is required' };
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      if (typeof op.domain !== 'string' || op.domain === '') return { ok: false, reason: 'domain is required' };
      return bounded(op.domain, 'domain');
    }
    case 'update-entity': {
      if (typeof op.entityId !== 'string' || op.entityId === '') {
        return { ok: false, reason: 'entityId is required' };
      }
      const eb = bounded(op.entityId, 'entityId');
      if (!eb.ok) return eb;
      return validateUpdatePatch(op, 'entityId', ['name', 'domain', 'description']);
    }
    case 'update-surface': {
      if (typeof op.surfaceId !== 'string' || op.surfaceId === '') {
        return { ok: false, reason: 'surfaceId is required' };
      }
      const sb = bounded(op.surfaceId, 'surfaceId');
      if (!sb.ok) return sb;
      if (op.surfaceType !== undefined && !VALID_SURFACE_TYPES.has(op.surfaceType as string)) {
        return { ok: false, reason: 'surfaceType is not a valid SurfaceType' };
      }
      return validateUpdatePatch(op, 'surfaceId', ['surfaceType', 'domain', 'hub', 'description']);
    }
    case 'update-cloud-function': {
      if (typeof op.name !== 'string' || op.name === '') {
        return { ok: false, reason: 'name is required' };
      }
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      if (op.functionKind !== undefined && !VALID_CLOUD_FUNCTION_KINDS.has(op.functionKind as string)) {
        return { ok: false, reason: 'functionKind is not a valid CloudFunctionKind' };
      }
      return validateUpdatePatch(op, 'name', ['functionKind', 'domain', 'description', 'rationale', 'triggerCondition']);
    }
    case 'update-event': {
      if (typeof op.type !== 'string' || op.type === '') {
        return { ok: false, reason: 'type is required' };
      }
      const tb = bounded(op.type, 'type');
      if (!tb.ok) return tb;
      if (op.retentionDays !== undefined && typeof op.retentionDays !== 'number') {
        return { ok: false, reason: 'retentionDays must be a number' };
      }
      return validateUpdatePatch(op, 'type', ['category', 'description', 'businessTrigger', 'retentionDays']);
    }
    case 'update-path': {
      if (typeof op.path !== 'string' || op.path === '') {
        return { ok: false, reason: 'path is required' };
      }
      const pb = bounded(op.path, 'path');
      if (!pb.ok) return pb;
      return validateUpdatePatch(op, 'path', ['retention', 'securityRuleNote', 'owner', 'quotaNotes']);
    }
    case 'add-payload-field': {
      if (typeof op.ownerId !== 'string' || op.ownerId === '') {
        return { ok: false, reason: 'ownerId is required' };
      }
      const ob = bounded(op.ownerId, 'ownerId');
      if (!ob.ok) return ob;
      if (!op.field || typeof op.field !== 'object') return { ok: false, reason: 'field is required' };
      if (typeof op.field.name !== 'string' || op.field.name === '') {
        return { ok: false, reason: 'field.name is required' };
      }
      if (typeof op.field.type !== 'string' || op.field.type === '') {
        return { ok: false, reason: 'field.type is required' };
      }
      const nb = bounded(op.field.name, 'field.name');
      if (!nb.ok) return nb;
      const tb = bounded(op.field.type, 'field.type');
      if (!tb.ok) return tb;
      if (op.field.description !== undefined) {
        const db = bounded(op.field.description, 'field.description');
        if (!db.ok) return db;
      }
      return { ok: true };
    }
    case 'remove-payload-field': {
      if (typeof op.ownerId !== 'string' || op.ownerId === '') {
        return { ok: false, reason: 'ownerId is required' };
      }
      if (typeof op.fieldName !== 'string' || op.fieldName === '') {
        return { ok: false, reason: 'fieldName is required' };
      }
      return bounded(op.fieldName, 'fieldName');
    }
    case 'rename-payload-field': {
      if (typeof op.ownerId !== 'string' || op.ownerId === '') {
        return { ok: false, reason: 'ownerId is required' };
      }
      if (typeof op.from !== 'string' || op.from === '') return { ok: false, reason: 'from is required' };
      if (typeof op.to !== 'string' || op.to === '') return { ok: false, reason: 'to is required' };
      const fb = bounded(op.from, 'from');
      if (!fb.ok) return fb;
      return bounded(op.to, 'to');
    }
    case 'change-payload-field-type': {
      if (typeof op.ownerId !== 'string' || op.ownerId === '') {
        return { ok: false, reason: 'ownerId is required' };
      }
      if (typeof op.fieldName !== 'string' || op.fieldName === '') {
        return { ok: false, reason: 'fieldName is required' };
      }
      if (typeof op.newType !== 'string' || op.newType === '') {
        return { ok: false, reason: 'newType is required' };
      }
      const fb = bounded(op.fieldName, 'fieldName');
      if (!fb.ok) return fb;
      return bounded(op.newType, 'newType');
    }
    case 'add-function-entity-ref':
    case 'remove-function-entity-ref': {
      if (typeof op.name !== 'string' || op.name === '') return { ok: false, reason: 'name is required' };
      const nb = bounded(op.name, 'name');
      if (!nb.ok) return nb;
      if (op.arrayName !== 'reads' && op.arrayName !== 'writes') {
        return { ok: false, reason: "arrayName must be 'reads' or 'writes'" };
      }
      if (typeof op.entityId !== 'string' || op.entityId === '') {
        return { ok: false, reason: 'entityId is required' };
      }
      return bounded(op.entityId, 'entityId');
    }
    case 'add-event-carried-entity':
    case 'remove-event-carried-entity': {
      if (typeof op.type !== 'string' || op.type === '') return { ok: false, reason: 'type is required' };
      const tb = bounded(op.type, 'type');
      if (!tb.ok) return tb;
      if (typeof op.entityId !== 'string' || op.entityId === '') {
        return { ok: false, reason: 'entityId is required' };
      }
      return bounded(op.entityId, 'entityId');
    }
    case 'link-feature-flag':
    case 'unlink-feature-flag': {
      if (typeof op.featureId !== 'string' || op.featureId === '') {
        return { ok: false, reason: 'featureId is required' };
      }
      const fib = bounded(op.featureId, 'featureId');
      if (!fib.ok) return fib;
      if (typeof op.flagId !== 'string' || op.flagId === '') {
        return { ok: false, reason: 'flagId is required' };
      }
      return bounded(op.flagId, 'flagId');
    }
    default:
      return { ok: false, reason: `unknown op kind: ${(op as { kind: string }).kind}` };
  }
}

/** Applies one add/remove/rename/change-type edit to a PayloadField[] array. Mirrors applyOp's Entity.fields logic. */
function applyPayloadFieldOp(
  fields: readonly { name: string; type: string; description?: string }[],
  op:
    | Extract<SandboxOp, { kind: 'add-payload-field' }>
    | Extract<SandboxOp, { kind: 'remove-payload-field' }>
    | Extract<SandboxOp, { kind: 'rename-payload-field' }>
    | Extract<SandboxOp, { kind: 'change-payload-field-type' }>,
): { name: string; type: string; description?: string }[] {
  switch (op.kind) {
    case 'add-payload-field':
      if (fields.some((f) => f.name === op.field.name)) return [...fields];
      return [
        ...fields,
        { name: op.field.name, type: op.field.type, ...(op.field.description ? { description: op.field.description } : {}) },
      ];
    case 'remove-payload-field':
      return fields.filter((f) => f.name !== op.fieldName);
    case 'rename-payload-field':
      return fields.map((f) => (f.name === op.from ? { ...f, name: op.to } : f));
    case 'change-payload-field-type':
      return fields.map((f) => (f.name === op.fieldName ? { ...f, type: op.newType } : f));
    default: {
      const _exhaustive: never = op;
      return _exhaustive;
    }
  }
}

/** Add/remove one entity id from a string[] ref array, idempotently. */
function applyEntityRefOp(items: readonly string[], entityId: string, mode: 'add' | 'remove'): string[] {
  if (mode === 'add') return items.includes(entityId) ? [...items] : [...items, entityId];
  return items.filter((id) => id !== entityId);
}

/**
 * Apply a list of ops to a manifest, producing a NEW manifest with the
 * affected records replaced or created. Never mutates the input. Ops
 * targeting unknown entities/fields, or creating an id that already
 * exists, are silently dropped — sandbox is a sketching tool, not a
 * validator.
 */
export function applyOps(manifest: AtlasManifest, ops: SandboxOp[]): AtlasManifest {
  if (ops.length === 0) return manifest;

  const byId = new Map<string, Entity>();
  for (const e of manifest.entities) byId.set(e.id, e);
  const domainsById = new Map<string, Domain>();
  for (const d of manifest.domains) domainsById.set(d.id, d);
  const surfacesById = new Map<string, Surface>();
  for (const s of manifest.surfaces) surfacesById.set(s.id, s);
  const functionsByName = new Map<string, CloudFunction>();
  for (const fn of manifest.cloudFunctions) functionsByName.set(fn.name, fn);
  const eventsByType = new Map<string, EventType>();
  for (const ev of manifest.events) eventsByType.set(ev.type, ev);
  const pathsByPath = new Map<string, FirestorePath>();
  for (const p of manifest.paths) pathsByPath.set(p.path, p);
  const tagsByTag = new Map<string, TagDefinition>();
  for (const t of manifest.tags) tagsByTag.set(t.tag, t);
  const machinesById = new Map<string, StateMachine>();
  for (const m of manifest.stateMachines) machinesById.set(m.id, m);
  const featuresById = new Map<string, Feature>();
  for (const f of manifest.features ?? []) featuresById.set(f.id, f);
  const references: Reference[] = [...manifest.references];

  for (const op of ops) {
    if (!validateOp(op).ok) continue;
    switch (op.kind) {
      case 'create-entity':
        if (!byId.has(op.entityId)) byId.set(op.entityId, buildNewEntity(op));
        break;
      case 'create-domain':
        if (!domainsById.has(op.domainId)) domainsById.set(op.domainId, buildNewDomain(op));
        break;
      case 'create-surface':
        if (!surfacesById.has(op.surfaceId)) surfacesById.set(op.surfaceId, buildNewSurface(op));
        break;
      case 'create-cloud-function':
        if (!functionsByName.has(op.name)) functionsByName.set(op.name, buildNewCloudFunction(op));
        break;
      case 'create-event':
        if (!eventsByType.has(op.eventType)) eventsByType.set(op.eventType, buildNewEvent(op));
        break;
      case 'create-path':
        if (!pathsByPath.has(op.path)) pathsByPath.set(op.path, buildNewPath(op));
        break;
      case 'create-tag':
        if (!tagsByTag.has(op.tag)) tagsByTag.set(op.tag, buildNewTag(op));
        break;
      case 'create-state-machine':
        if (!machinesById.has(op.machineId)) machinesById.set(op.machineId, buildNewStateMachine(op));
        break;
      case 'create-reference': {
        const exists = references.some(
          (r) => r.from === op.from && r.to === op.to && r.fromField === op.fromField,
        );
        if (!exists) references.push(buildNewReference(op));
        break;
      }
      case 'update-entity': {
        const existing = byId.get(op.entityId);
        if (existing) {
          byId.set(op.entityId, {
            ...existing,
            ...(op.name !== undefined ? { name: op.name } : {}),
            ...(op.domain !== undefined ? { domain: op.domain } : {}),
            ...(op.description !== undefined ? { description: op.description } : {}),
          });
        }
        break;
      }
      case 'update-surface': {
        const existing = surfacesById.get(op.surfaceId);
        if (existing) {
          surfacesById.set(op.surfaceId, {
            ...existing,
            ...(op.surfaceType !== undefined ? { type: op.surfaceType } : {}),
            ...(op.domain !== undefined ? { domain: op.domain } : {}),
            ...(op.hub !== undefined ? { hub: op.hub } : {}),
            ...(op.description !== undefined ? { description: op.description } : {}),
          });
        }
        break;
      }
      case 'update-cloud-function': {
        const existing = functionsByName.get(op.name);
        if (existing) {
          functionsByName.set(op.name, {
            ...existing,
            ...(op.functionKind !== undefined ? { kind: op.functionKind } : {}),
            ...(op.domain !== undefined ? { domain: op.domain } : {}),
            ...(op.description !== undefined ? { description: op.description } : {}),
            ...(op.rationale !== undefined ? { rationale: op.rationale } : {}),
            ...(op.triggerCondition !== undefined ? { triggerCondition: op.triggerCondition } : {}),
          });
        }
        break;
      }
      case 'update-event': {
        const existing = eventsByType.get(op.type);
        if (existing) {
          eventsByType.set(op.type, {
            ...existing,
            ...(op.category !== undefined ? { category: op.category } : {}),
            ...(op.description !== undefined ? { description: op.description } : {}),
            ...(op.businessTrigger !== undefined ? { businessTrigger: op.businessTrigger } : {}),
            ...(op.retentionDays !== undefined ? { retentionDays: op.retentionDays } : {}),
          });
        }
        break;
      }
      case 'update-path': {
        const existing = pathsByPath.get(op.path);
        if (existing) {
          pathsByPath.set(op.path, {
            ...existing,
            ...(op.retention !== undefined ? { retention: op.retention } : {}),
            ...(op.securityRuleNote !== undefined ? { securityRuleNote: op.securityRuleNote } : {}),
            ...(op.owner !== undefined ? { owner: op.owner } : {}),
            ...(op.quotaNotes !== undefined ? { quotaNotes: op.quotaNotes } : {}),
          });
        }
        break;
      }
      case 'add-payload-field':
      case 'remove-payload-field':
      case 'rename-payload-field':
      case 'change-payload-field-type': {
        if (op.ownerKind === 'surface') {
          const existing = surfacesById.get(op.ownerId);
          if (existing) {
            surfacesById.set(op.ownerId, { ...existing, payloadFields: applyPayloadFieldOp(existing.payloadFields, op) });
          }
        } else {
          const existing = eventsByType.get(op.ownerId);
          if (existing) {
            eventsByType.set(op.ownerId, { ...existing, payloadFields: applyPayloadFieldOp(existing.payloadFields, op) });
          }
        }
        break;
      }
      case 'add-function-entity-ref':
      case 'remove-function-entity-ref': {
        const existing = functionsByName.get(op.name);
        if (existing) {
          const mode = op.kind === 'add-function-entity-ref' ? 'add' : 'remove';
          functionsByName.set(op.name, {
            ...existing,
            [op.arrayName]: applyEntityRefOp(existing[op.arrayName], op.entityId, mode),
          });
        }
        break;
      }
      case 'add-event-carried-entity':
      case 'remove-event-carried-entity': {
        const existing = eventsByType.get(op.type);
        if (existing) {
          const mode = op.kind === 'add-event-carried-entity' ? 'add' : 'remove';
          eventsByType.set(op.type, { ...existing, carriedEntities: applyEntityRefOp(existing.carriedEntities, op.entityId, mode) });
        }
        break;
      }
      case 'link-feature-flag':
      case 'unlink-feature-flag': {
        const existing = featuresById.get(op.featureId);
        if (existing) {
          const mode = op.kind === 'link-feature-flag' ? 'add' : 'remove';
          featuresById.set(op.featureId, {
            ...existing,
            gatingFlagIds: applyEntityRefOp(existing.gatingFlagIds ?? [], op.flagId, mode),
          });
        }
        break;
      }
      default: {
        const entity = byId.get(op.entityId);
        if (entity) byId.set(op.entityId, applyOp(entity, op));
      }
    }
  }

  return {
    ...manifest,
    entities: [...byId.values()],
    domains: [...domainsById.values()],
    surfaces: [...surfacesById.values()],
    cloudFunctions: [...functionsByName.values()],
    events: [...eventsByType.values()],
    paths: [...pathsByPath.values()],
    tags: [...tagsByTag.values()],
    stateMachines: [...machinesById.values()],
    features: [...featuresById.values()],
    references,
  };
}

/** Build a brand-new entity from a create-entity op — a sandbox proposal,
 *  not yet validated/mocked/committed/implemented. */
function buildNewEntity(op: Extract<SandboxOp, { kind: 'create-entity' }>): Entity {
  return {
    id: op.entityId,
    name: op.name,
    domain: op.domain,
    docRef: '',
    docSection: '',
    description: op.description,
    fields: [],
    tags: [],
    surfaces: [],
    events: [],
    isPersisted: false,
    isEphemeral: true,
    isMinorVisible: false,
    kind: 'aggregate',
    lifecycle: 'proposed',
  };
}

/** Build a brand-new domain from a create-domain op. */
function buildNewDomain(op: Extract<SandboxOp, { kind: 'create-domain' }>): Domain {
  return {
    id: op.domainId,
    name: op.name,
    color: op.color,
    docRef: '',
    description: op.description,
    entityCount: 0,
  };
}

/** Build a brand-new surface from a create-surface op. */
function buildNewSurface(op: Extract<SandboxOp, { kind: 'create-surface' }>): Surface {
  return {
    id: op.surfaceId,
    type: op.surfaceType,
    domain: op.domain,
    payloadType: '',
    payloadFields: [],
    hub: op.hub,
    isMinorVisible: false,
  };
}

/** Build a brand-new cloud function from a create-cloud-function op. */
function buildNewCloudFunction(op: Extract<SandboxOp, { kind: 'create-cloud-function' }>): CloudFunction {
  return {
    name: op.name,
    kind: op.functionKind,
    reads: [],
    writes: [],
    domain: op.domain,
  };
}

/** Build a brand-new event type from a create-event op. */
function buildNewEvent(op: Extract<SandboxOp, { kind: 'create-event' }>): EventType {
  return {
    type: op.eventType,
    category: op.category,
    schemaVersion: 1,
    payloadFields: [],
    carriedEntities: [],
    safetyTier: op.safetyTier,
  };
}

/** Build a brand-new Firestore path from a create-path op. */
function buildNewPath(op: Extract<SandboxOp, { kind: 'create-path' }>): FirestorePath {
  return {
    path: op.path,
    entityId: op.entityId,
    retention: op.retention,
  };
}

/** Build a brand-new tag definition from a create-tag op. */
function buildNewTag(op: Extract<SandboxOp, { kind: 'create-tag' }>): TagDefinition {
  return {
    tag: op.tag,
    description: op.description,
    count: 0,
  };
}

/** Build a brand-new state machine from a create-state-machine op. */
function buildNewStateMachine(op: Extract<SandboxOp, { kind: 'create-state-machine' }>): StateMachine {
  return {
    id: op.machineId,
    name: op.name,
    domain: op.domain,
    states: [],
    transitions: [],
    triggeredByEvents: [],
    mutatesEntities: [],
    surfaces: [],
  };
}

/** Build a brand-new reference edge from a create-reference op. */
function buildNewReference(op: Extract<SandboxOp, { kind: 'create-reference' }>): Reference {
  return {
    from: op.from,
    to: op.to,
    fromField: op.fromField,
    kind: op.referenceKind,
    optional: false,
  };
}

/**
 * Applies every op from a pending-ops ledger (packages/atlas-manifest/pending-ops.json)
 * on top of a freshly-parsed manifest, at build time. Pure — the caller reads the
 * file and passes its raw content (or null if the file doesn't exist yet, the
 * expected pre-first-publish state).
 *
 * A present-but-unparseable ledger is a hard failure (throws) rather than a
 * silent skip — silently ignoring corrupted ledger data would mean published
 * changes silently vanish from the manifest, which is worse than failing loud.
 * An individual invalid op within an otherwise-valid entry is dropped (and
 * reported via onWarn) rather than failing the whole entry, matching
 * applyOps's own existing per-op validation behavior.
 */
export function applyPendingOps(
  manifest: AtlasManifest,
  ledgerJson: string | null,
  onWarn?: (msg: string) => void,
): AtlasManifest {
  if (ledgerJson === null) return manifest;

  const ledger = JSON.parse(ledgerJson) as PendingOpsLedger;
  const allOps: SandboxOp[] = [];
  for (const entry of ledger.entries) {
    for (const op of entry.ops) {
      if (validateOp(op).ok) {
        allOps.push(op);
      } else {
        onWarn?.(`dropped invalid op in published sandbox "${entry.sandboxName}" (${entry.sandboxId}): ${JSON.stringify(op)}`);
      }
    }
  }
  return applyOps(manifest, allOps);
}

type FieldOp = Exclude<
  SandboxOp,
  | { kind: 'create-entity' }
  | { kind: 'create-domain' }
  | { kind: 'create-reference' }
  | { kind: 'create-surface' }
  | { kind: 'create-cloud-function' }
  | { kind: 'create-event' }
  | { kind: 'create-path' }
  | { kind: 'create-tag' }
  | { kind: 'create-state-machine' }
  | { kind: 'update-entity' }
  | { kind: 'update-surface' }
  | { kind: 'update-cloud-function' }
  | { kind: 'update-event' }
  | { kind: 'update-path' }
  | { kind: 'add-payload-field' }
  | { kind: 'remove-payload-field' }
  | { kind: 'rename-payload-field' }
  | { kind: 'change-payload-field-type' }
  | { kind: 'add-function-entity-ref' }
  | { kind: 'remove-function-entity-ref' }
  | { kind: 'add-event-carried-entity' }
  | { kind: 'remove-event-carried-entity' }
  | { kind: 'link-feature-flag' }
  | { kind: 'unlink-feature-flag' }
>;

function applyOp(entity: Entity, op: FieldOp): Entity {
  switch (op.kind) {
    case 'add-field': {
      // Skip when a field of that name already exists (idempotent).
      if (entity.fields.some((f) => f.name === op.field.name)) return entity;
      const newField: Field = {
        name: op.field.name,
        type: op.field.type,
        optional: op.field.optional,
        ...(op.field.description ? { description: op.field.description } : {}),
      };
      return { ...entity, fields: [...entity.fields, newField] };
    }
    case 'remove-field':
      return { ...entity, fields: entity.fields.filter((f) => f.name !== op.fieldName) };
    case 'rename-field':
      return {
        ...entity,
        fields: entity.fields.map((f) =>
          f.name === op.from ? { ...f, name: op.to } : f,
        ),
      };
    case 'change-type':
      return {
        ...entity,
        fields: entity.fields.map((f) =>
          f.name === op.fieldName ? { ...f, type: op.newType } : f,
        ),
      };
  }
}
